/**
 * This hook returns a preset list of utm query params, from the url.
 *
 * It also accepts a list of `matchParams`, and returns a boolean
 * to show if at least 1 of the values passed in for each key is found.
 *
 * The goal is to make it easy to:
 * 1. get utm params from the url
 * 2. determine if a match is made
 *
 * When found, writes the params to session storage, to maintain state.
 * This can optionally be turned off, by setting `readOnly`.
 */

import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { clearState, loadState, saveState } from '@/utils/stateStorage';

import type { PartnerUtm } from './partner/types';

interface UsePartnerUtmOptions extends PartnerUtm {
  readOnly?: boolean; // optionally do not write to storage
}

interface UsePartnerUtmReturn {
  utm: PartnerUtm | null; // return the object, for debugging
  isMatch: boolean; // at least 1 match for each param is found
  isMatchParams: boolean; // match is from query (not storage)
  isMatchStorage: boolean; // match is from storage (not query)
  clear: () => void; // clear storage
}

interface UsePartnerUtmState {
  utm: PartnerUtm | null;
  hasQueryUtm: boolean;
}

export const STORAGE_KEY = 'parner-utm-data';
const INIT_STATE: UsePartnerUtmState = {
  utm: null,
  hasQueryUtm: false,
};

const usePartnerUtm = ({
  readOnly,
  ...matchParams
}: UsePartnerUtmOptions = {}): UsePartnerUtmReturn => {
  const stateUtm = loadState(STORAGE_KEY) as PartnerUtm;
  const [state, setState] = useState<UsePartnerUtmState>({
    ...INIT_STATE,
    utm: stateUtm,
  });
  const params = useSearchParams();

  useEffect(() => {
    const utmMedium = params.get('utm_medium');
    const utmSource = params.get('utm_source');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const queryUtm = {
      utm_medium: utmMedium,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
    } as PartnerUtm;

    const hasQueryUtm = Object.values(queryUtm).some((utm) => utm);

    if (hasQueryUtm && !readOnly) {
      saveState(queryUtm, STORAGE_KEY);
    }

    const utm = hasQueryUtm ? queryUtm : state.utm;

    if (!isEqual(state, { utm, hasQueryUtm })) {
      setState({ utm, hasQueryUtm });
    }
  }, [params, readOnly, state]);

  // check if all matchParams are present
  const isMatch = Object.keys(matchParams).length
    ? Object.entries(matchParams).every(([key, val]) => {
        // for comparison, cast to array (query can have the same param multiple times, which returns an array)
        const _utm = state.utm;
        const utmValue = _utm?.[key as keyof typeof _utm];
        const utmList = Array.isArray(utmValue) ? utmValue : [utmValue];
        const valuesList = Array.isArray(val) ? val : [val];

        return intersection(utmList, valuesList).length;
      })
    : false;

  // manually clear local storage & state
  const clear = () => {
    clearState(STORAGE_KEY);
    setState(INIT_STATE);
  };

  return {
    utm: state.utm,
    isMatch,
    isMatchParams: isMatch && state.hasQueryUtm,
    isMatchStorage: isMatch && !state.hasQueryUtm,
    clear,
  };
};

export default usePartnerUtm;
